import { useEffect, useState } from 'react';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { Box } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { getColumnHeadersAdvanced } from './getStationsPageTableColumnHeaders.js' 
import { loadUserShownColumns, loadUserFilter, loadUserColumnWidths } from './StationsPageTableCachingUtils.js'
import { fetchChannelDataRows } from '../../utilities/apiRequests.js';
import { useHandleResizeError } from '../../utilities/handleResizeError.js';
import { getPaginationStyle, getToolbarStyle } from './StationsPageTableSubComponentStyling.js';
import { useNavigate } from 'react-router';
import { getPlatform } from "../../utilities/orientationAndPlatform.js";
import { useTheme } from '@mui/material/styles';


export default function StationsPageTableAdvanced() {

  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const theme = useTheme();

  let boxStyling = {
    backgroundColor: 'TableCell.background',
    border: theme.palette.TableCell.border,
    hoverColor: 'palette.TableCell.hover',
  }

  // fetch rows from api
  const [rows, setRows] = useState({});
  useEffect(() => {
    getAccessTokenSilently({ authorizationParams: { scope: "read:channels" } })
      .then((accessToken) => {
        // only fetch data for the columns that are shown
        const fields = [];
        for (const field of Object.entries(loadUserShownColumns())) {
          if (field[1]) {
            fields.push(field[0]);
          }
        }
        return fetchChannelDataRows(accessToken, fields);
      })
      .then((fetchedRows) => setRows(fetchedRows))
      .catch((error) => console.error("Error fetching rows:", error));
  }, [getAccessTokenSilently]);

  //Handle the saving and loading of show/hide state of columns from browser cache
  const savedUserShownColumns = loadUserShownColumns();
  const [currentUserShownColumns, setCurrentUserShownColumns] = useState(savedUserShownColumns)
  useEffect(() => {}, [currentUserShownColumns]);
  async function onColumnVisibilityChange(updatedUserShownColumns){
    try {
      const fields = Object.keys(updatedUserShownColumns).filter((key) => updatedUserShownColumns[key]);
      const updatedRows = await fetchChannelDataRows(await getAccessTokenSilently({ authorizationParams: { scope: "read:channels" } }), fields);
      setRows(updatedRows);
      setCurrentUserShownColumns(updatedUserShownColumns);
      localStorage.setItem("savedUserShownColumns", JSON.stringify(updatedUserShownColumns));
    } catch (error) {
      console.error("Error fetching rows:", error);
    }
  }

  useHandleResizeError()

  //state variables for sizing options
  const [sizeStatus, setSizeStatus] = useState('compact');
  const [sizeVal, setSizeVal] = useState("1em");

  //called when size menu's selected option changes, sets various state variables having to do with font sizing
  function handleSizeChange(newSizeStatus){
    if (newSizeStatus === 'compact') setSizeVal("1rem")
    else if (newSizeStatus === 'standard') setSizeVal("1.5rem")
    else if (newSizeStatus === 'comfortable') setSizeVal("2rem")
    setSizeStatus(newSizeStatus)
  }

  //Handle the saving and loading of column width state from browser cache
  const savedUserColumnWidths = loadUserColumnWidths();
  function onColumnResize(resizedColumn){
    let resizedColumnField = resizedColumn.colDef.field
    let newWidth = resizedColumn.colDef.computedWidth
    savedUserColumnWidths[resizedColumnField] = newWidth
    localStorage.setItem("savedUserColumnWidths", JSON.stringify(savedUserColumnWidths))
  }

  //Handle the saving and loading of the filter option state from browser cache
  const savedUserFilter = loadUserFilter();
  const [currentUserFilter, setCurrentUserFilter] = useState(savedUserFilter)
  useEffect(() => {}, [currentUserFilter]);
  function onFilterModelChange(updatedUserFilter){
    setCurrentUserFilter(updatedUserFilter)
    localStorage.setItem("savedUserFilter", JSON.stringify(updatedUserFilter))
  }

  function handleRowClick(params, event){
    event.preventDefault()

    let cellTypeClicked = event.target.className
    let cellValueClicked = event.target.dataset.field

    if (cellTypeClicked === 'WebsiteCell'){
          const href = event.target.href
          if(href){
            window.open(event.target.href)
          }
          else{
            console.warn("No href found for WebsiteCell")
            navigate(`/channelpage/${params.id}`)
          }
    } 
    else if (cellValueClicked === 'website_vod_assets') {
      const anchor = event.target.querySelector('a')
      if(anchor && anchor.href){
        window.open(event.target.firstChild.href) 
      }
      else{
        console.warn("No <a> href found inside website cell")
        navigate(`/channelpage/${params.id}`)
      }
    }
    else navigate(`/channelpage/${params.id}`)
  }


  const columnHeaders = getColumnHeadersAdvanced();

  let platform = getPlatform()
  let topPadding = ''
  if (platform === 'mobile') topPadding = '0.25em'
  else if (platform === 'desktop') topPadding = '0.5em'
  const paginationStyle = getPaginationStyle()
  const toolbarStyle = getToolbarStyle()

  
  return (
    <Box sx={{paddingTop: topPadding, width: "97.5%"}} id={"StationsPageTableAdvancedBox"}>
      <DataGrid
        //data
        rows={rows}
        onRowClick={handleRowClick}
        getRowId={(row) => row.channel_id}
        columns={columnHeaders}
        //Formatting
        getRowHeight={() => "auto"} 
        sx={{
          fontSize: sizeVal,
          border: 'none',
          whiteSpace: 'normal',
          wordWrap: 'break-word',
          backgroundColor: boxStyling.backgroundColor,
          '& .MuiDataGrid-cell':{
            borderRight: boxStyling.border,
            borderBottom: boxStyling.border,
          },    
          '& .MuiDataGrid-row:hover': {
            cursor: 'pointer',
            backgroundColor: boxStyling.hoverColor,
          },       
          '& .MuiDataGrid-footerContainer': {
              border: 'none',
            }   
        }}
        //Filtering
        filterModel={currentUserFilter}
        onFilterModelChange={onFilterModelChange}
        localeText={{ toolbarFilters: "Filter", toolbarDensity: "Size"}} //Change filter button text from Filters to Filter
        //Column Show/Hide
        columnVisibilityModel={currentUserShownColumns}
        onColumnVisibilityModelChange={(updatedUserShownColumns) => onColumnVisibilityChange(updatedUserShownColumns)}
        //Column width resize
        onColumnResize={(resizedColumn) => onColumnResize(resizedColumn)}
        //size menu
        density={sizeStatus}
        onDensityChange={(newSizeStatus) => handleSizeChange(newSizeStatus)}
        //disable unused options
        slotProps={{ 
          columnsManagement: { disableShowHideToggle: true, disableResetButton: true, },
          pagination: {sx: paginationStyle},//footer styling for proper screen fitting
          toolbar: {sx: toolbarStyle},
          filterPanel: {sx: {maxWidth: '100vw'}}
        }}
        hideFooterSelectedRowCount={true}
        //Pagination options
        initialState={{pagination: { paginationModel: { page: 0, pageSize: 15}}}}
        pageSizeOptions={[15, 25, 50, 100, { value: -1, label: 'All' }]}
        //Enable toolbar
        slots={{
          toolbar: GridToolbar,
          toolbarContainer: { sx: {
            '& .MuiDataGrid-toolbarContainer': {
              padding: '0 !important',
            },
          }},
      }}
      />
    </Box>
  );
}
